import React from "react";

// KundaliWidget.jsx
// A reusable React component that renders a North-Indian style Kundali (diamond) chart as an SVG.
// - Uses props for data, size and simple styling.
// - Tailwind-friendly container (no Tailwind import required).
// - Default sample data is provided.

// Props:
// - size: number (px width and height of the SVG). Default 480.
// - houses: array of 12 objects { number: 1..12, label: 'Sun', color: '#...' }
// - showNumbers: boolean - show house numbers (default true)
// - fontSize: number - base font size for labels

export default function KundaliWidget({
  size = 480,
  houses = DEFAULT_HOUSES,
  showNumbers = true,
  fontSize = 14,
  stroke = '#333',
  strokeWidth = 2,
  background = '#fff',
}) {
  // The classic north-indian kundali has a diamond divided into 12 triangular/curved cells.
  // We'll draw the diamond with 4 diagonal lines and a small central square for aesthetic.

  const half = size / 2;
  const pad = Math.round(size * 0.06);
  const inner = half - pad;

  // Coordinates for the diamond points
  const top = { x: half, y: pad };
  const right = { x: size - pad, y: half };
  const bottom = { x: half, y: size - pad };
  const left = { x: pad, y: half };

  // central small square for numbers/center
  const cs = Math.round(size * 0.08);
  const cLeft = half - cs / 2;
  const cTop = half - cs / 2;

  // Houses order used in many north-indian charts (1 at top center diamond cell then counter-clockwise):
  // We'll map each house to a polygon path (approximate diamond subdivisions). We'll create 12 polygon regions.
  // For readability we calculate midpoints between center and corners and edges.

  const midTopLeft = { x: (top.x + left.x) / 2 - cs * 0.12, y: (top.y + left.y) / 2 - cs * 0.02 };
  const midTopRight = { x: (top.x + right.x) / 2 + cs * 0.12, y: (top.y + right.y) / 2 - cs * 0.02 };
  const midRightTop = { x: (right.x + top.x) / 2 + cs * 0.02, y: (right.y + top.y) / 2 - cs * 0.12 };
  const midRightBottom = { x: (right.x + bottom.x) / 2 + cs * 0.02, y: (right.y + bottom.y) / 2 + cs * 0.12 };
  const midBottomRight = { x: (bottom.x + right.x) / 2 + cs * 0.12, y: (bottom.y + right.y) / 2 + cs * 0.02 };
  const midBottomLeft = { x: (bottom.x + left.x) / 2 - cs * 0.12, y: (bottom.y + left.y) / 2 + cs * 0.02 };
  const midLeftBottom = { x: (left.x + bottom.x) / 2 - cs * 0.02, y: (left.y + bottom.y) / 2 + cs * 0.12 };
  const midLeftTop = { x: (left.x + top.x) / 2 - cs * 0.02, y: (left.y + top.y) / 2 - cs * 0.12 };

  // We'll create label positions for houses 1..12 around the diamond. These have been adjusted by eye for good placement.
  const labelPositions = {
    1: { x: (top.x + midTopRight.x) / 2, y: (top.y + midTopRight.y) / 2 - 6 },
    2: { x: (midTopRight.x + right.x) / 2 + 6, y: (midTopRight.y + right.y) / 2 - 6 },
    3: { x: (right.x + midRightBottom.x) / 2 + 6, y: (right.y + midRightBottom.y) / 2 + 4 },
    4: { x: (midRightBottom.x + bottom.x) / 2 + 6, y: (midRightBottom.y + bottom.y) / 2 + 6 },
    5: { x: (bottom.x + midBottomLeft.x) / 2, y: (bottom.y + midBottomLeft.y) / 2 + 10 },
    6: { x: (midBottomLeft.x + left.x) / 2 - 8, y: (midBottomLeft.y + left.y) / 2 + 6 },
    7: { x: (left.x + midLeftTop.x) / 2 - 8, y: (left.y + midLeftTop.y) / 2 - 6 },
    8: { x: (midLeftTop.x + top.x) / 2 - 6, y: (midLeftTop.y + top.y) / 2 - 6 },
    9: { x: half - 0, y: half + 4 }, // roughly center
    10: { x: (midTopRight.x + right.x) / 2 + 8, y: (midTopRight.y + right.y) / 2 + 18 },
    11: { x: (midRightBottom.x + bottom.x) / 2 + 18, y: (midRightBottom.y + bottom.y) / 2 + 20 },
    12: { x: (top.x + right.x) / 2 + 4, y: (top.y + right.y) / 2 + 4 },
  };

  // For a reliable mapping we'll manually choose 12 polygon outlines that visually match standard layout.
  // To keep things straightforward, draw the outer diamond and two diagonals then compute the 12 text positions around edges.

  return (
    <div className="p-2" style={{ width: size, maxWidth: '100%' }}>
      <div className="rounded shadow-sm" style={{ background }}>
        <svg
          viewBox={`0 0 ${size} ${size}`}
          width={size}
          height={size}
          role="img"
          aria-label="Kundali chart"
        >
          {/* Background */}
          <rect x={0} y={0} width={size} height={size} fill={background} />

          {/* Diamond outline */}
          <polyline
            points={`${half},${pad} ${size - pad},${half} ${half},${size - pad} ${pad},${half} ${half},${pad}`}
            fill="none"
            stroke={stroke}
            strokeWidth={strokeWidth}
          />

          {/* Central small square */}
          <rect
            x={cLeft}
            y={cTop}
            width={cs}
            height={cs}
            fill="transparent"
            stroke={stroke}
            strokeWidth={Math.max(1, strokeWidth - 0.5)}
            rx={4}
          />

          {/* Diagonals from corners to center (gives the 4 main cells) */}
          <line x1={top.x} y1={top.y} x2={half} y2={half} stroke={stroke} strokeWidth={strokeWidth} />
          <line x1={right.x} y1={right.y} x2={half} y2={half} stroke={stroke} strokeWidth={strokeWidth} />
          <line x1={bottom.x} y1={bottom.y} x2={half} y2={half} stroke={stroke} strokeWidth={strokeWidth} />
          <line x1={left.x} y1={left.y} x2={half} y2={half} stroke={stroke} strokeWidth={strokeWidth} />

          {/* Additional decorative curves (approx) to mimic style in reference */}
          <path
            d={`M ${pad + 10},${half} Q ${half},${pad + 10} ${size - pad - 10},${half}`}
            fill="none"
            stroke={stroke}
            strokeWidth={1}
            opacity={0.18}
          />
          <path
            d={`M ${half},${pad + 10} Q ${size - pad - 10},${half} ${half},${size - pad - 10}`}
            fill="none"
            stroke={stroke}
            strokeWidth={1}
            opacity={0.08}
          />

          {/* House labels (text). We'll place them around the diamond. The houses prop should be an array of 12 items - we'll map index 0->house1 */}
          {houses.slice(0, 12).map((h, i) => {
            const houseNumber = i + 1;
            // Fallbacks
            const label = h && (h.label || h.name || h.text) ? (h.label || h.name || h.text) : '';
            const color = h && h.color ? h.color : '#111';

            // use defined labelPositions if exists; else position around circle
            const pos = labelPositions[houseNumber] || {
              x: half + Math.cos((i / 12) * Math.PI * 2) * (inner * 0.7),
              y: half + Math.sin((i / 12) * Math.PI * 2) * (inner * 0.7),
            };

            return (
              <g key={`h-${houseNumber}`}>
                {showNumbers && (
                  <text
                    x={pos.x}
                    y={pos.y - (label ? fontSize * 0.9 : 0)}
                    fontSize={Math.max(10, fontSize * 0.85)}
                    fontFamily="Inter, Roboto, system-ui, sans-serif"
                    textAnchor="middle"
                    fill="#666"
                    style={{ pointerEvents: 'none' }}
                  >
                    {houseNumber}
                  </text>
                )}

                {label && (
                  <text
                    x={pos.x}
                    y={pos.y + (showNumbers ? fontSize * 0.2 : 0)}
                    fontSize={fontSize}
                    fontFamily="Inter, Roboto, system-ui, sans-serif"
                    textAnchor="middle"
                    fill={color}
                    fontWeight={600}
                    style={{ pointerEvents: 'none' }}
                  >
                    {label}
                  </text>
                )}
              </g>
            );
          })}

          {/* Optional small watermark center number (for demonstration) */}
          <text
            x={half}
            y={half + cs / 4}
            fontSize={Math.max(12, fontSize * 0.85)}
            textAnchor="middle"
            fill="#bbb"
          >
            Kundali
          </text>
        </svg>
      </div>
    </div>
  );
}

// Default sample dataset for quick preview
const DEFAULT_HOUSES = [
  { label: 'Ura', color: '#1E90FF' }, // 1
  { label: 'Ven', color: '#2E8B57' }, // 2
  { label: '', color: '#000' }, // 3
  { label: 'Jup', color: '#C71585' }, // 4
  { label: 'Ketu', color: '#A52A2A' }, // 5
  { label: '', color: '#000' }, // 6
  { label: 'Mar\nSun', color: '#D2691E' }, // 7
  { label: 'Moon\nMer', color: '#8B4513' }, // 8
  { label: '', color: '#000' }, // 9
  { label: 'Plu', color: '#8B008B' }, // 10
  { label: 'Rahu', color: '#000000' }, // 11
  { label: 'Sat\nNep', color: '#4B0082' }, // 12
];

/*
Usage example in a React app:

import KundaliWidget from './KundaliWidget';

function App(){
  const myHouses = [
    { label: 'Ura', color: '#007bff'},
    { label: 'Ven', color: '#2ecc71'},
    // ... 12 items total
  ];

  return <KundaliWidget size={520} houses={myHouses} fontSize={14} />;
}

*/
 